import { NavigateFunction } from 'react-router-dom';
import { EMBED_PARAM_KEYS } from '../hooks/useEmbedParams';

/**
 * Utility function to navigate while preserving embed parameters
 *
 * @param navigate - React Router navigate function
 * @param path - The path to navigate to
 * @param preserveEmbedParams - Whether to preserve embed-related query parameters (default: true)
 * @param options - Navigation options (e.g., replace)
 *
 * @example
 * navigatePreservingParams(navigate, '/admin/users', true, { replace: true });
 */
export const navigatePreservingParams = (
  navigate: NavigateFunction,
  path: string,
  preserveEmbedParams: boolean = true,
  options?: { replace?: boolean }
): void => {
  if (!path) {
    return;
  }

  if (!preserveEmbedParams) {
    navigate(path, options);
    return;
  }

  const finalPath = appendEmbedParamsToPath(path);

  navigate(finalPath, options);
};

/**
 * Utility function to extract embed parameters from current URL
 *
 * @returns URLSearchParams containing only embed parameters
 *
 * @example
 * const embedParams = getEmbedParams();
 * const embedValue = embedParams.get('embed');
 */
export const getEmbedParams = (): URLSearchParams => {
  const currentParams = new URLSearchParams(window.location.search);
  const embedParams = new URLSearchParams();

  EMBED_PARAM_KEYS.forEach((key) => {
    const value = currentParams.get(key);
    if (value !== null) {
      embedParams.set(key, value);
    }
  });

  return embedParams;
};

/**
 * Utility function to get current embed parameters as a query string
 *
 * @returns Query string with embed parameters (without leading ?)
 *
 * @example
 * const queryString = getEmbedParamsQueryString();
 * // Returns: "embed=true&source=dashboard&userAlias=jdoe"
 */
export const getEmbedParamsQueryString = (): string => {
  return getEmbedParams().toString();
};

/**
 * Parses a path into its components (base path, query params, fragment)
 *
 * @param path - The path to parse
 * @returns Object containing basePath, params, and fragment
 *
 * @internal
 */
const parsePath = (path: string): { basePath: string; params: URLSearchParams; fragment: string } => {
  const [pathWithoutFragment, fragment = ''] = path.split('#');
  const [basePath, existingParamsString = ''] = pathWithoutFragment.split('?');
  const params = new URLSearchParams(existingParamsString);

  return { basePath, params, fragment };
};

/**
 * Constructs a path from its components
 *
 * @param basePath - The base path without query params or fragment
 * @param params - URLSearchParams object
 * @param fragment - The fragment (hash) part of the URL
 * @returns Constructed path
 *
 * @internal
 */
const constructPath = (basePath: string, params: URLSearchParams, fragment: string): string => {
  const paramsString = params.toString();
  const queryPart = paramsString ? `?${paramsString}` : '';
  const fragmentPart = fragment ? `#${fragment}` : '';

  return `${basePath}${queryPart}${fragmentPart}`;
};

/**
 * Utility function to append embed parameters to a given path
 *
 * @param path - The base path
 * @returns Path with embed parameters appended
 *
 * @example
 * const newPath = appendEmbedParamsToPath('/admin/users?tab=active');
 * // Returns: "/admin/users?tab=active&embed=true&source=dashboard"
 */
export const appendEmbedParamsToPath = (path: string): string => {
  if (!path) {
    return '';
  }

  const { basePath, params, fragment } = parsePath(path);

  // Get current embed params and merge with existing params
  const embedParams = getEmbedParams();
  EMBED_PARAM_KEYS.forEach((key) => {
    const value = embedParams.get(key);
    if (value !== null) {
      params.set(key, value);
    }
  });

  return constructPath(basePath, params, fragment);
};
